import { useState, useEffect } from "react";
import { useParams, Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Check, Leaf, ArrowRight, ArrowLeft, FileDown, Loader2, CheckCircle, Eye, CalendarIcon, Save, Plus, Trash2 } from "lucide-react";
import { format } from "date-fns";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import AppHeader from "@/components/AppHeader";
import { MCS_FORMS } from "@/config/mcsFormConfigs";
import { saveDraft, loadDraft, deleteDraft } from "@/utils/offlineStorage";
import { queueForSync } from "@/utils/syncQueue";
import BuyCreditsModal from "@/components/BuyCreditsModal";
import { useCreditPrompt } from "@/hooks/useCreditPrompt";
import PdfPreviewModal from "@/components/PdfPreviewModal";
import SignaturePad from "@/components/SignaturePad";
import TemplateBar, { SaveTemplateButton } from "@/components/TemplateBar";
import { authFetch } from "@/utils/authFetch";

const API = process.env.REACT_APP_BACKEND_URL;

const Label = ({ children }) => (
  <label className="block text-xs uppercase tracking-[0.15em] font-bold text-[#71717A] mb-1.5" style={{ fontFamily: 'Outfit, sans-serif' }}>{children}</label>
);

function FormField({ field, value, onChange }) {
  if (field.type === "checkbox") {
    return (
      <label className="flex items-start gap-3 py-2 cursor-pointer group" data-testid={`mcs-field-${field.id}`}>
        <Checkbox
          checked={!!value}
          onCheckedChange={(c) => onChange(!!c)}
          className="mt-0.5 w-5 h-5 border-2 border-[#0A0A0A] rounded-none data-[state=checked]:bg-[#22C55E] data-[state=checked]:border-[#22C55E] flex-shrink-0"
        />
        <span className="text-sm leading-snug group-hover:text-[#0A0A0A] text-[#3F3F46]">{field.label}</span>
      </label>
    );
  }
  if (field.type === "select") {
    return (
      <div className="form-field" data-testid={`mcs-field-${field.id}`}>
        <Label>{field.label}</Label>
        <select className="brutalist-input" value={value || ""} onChange={(e) => onChange(e.target.value)}>
          <option value="">Select...</option>
          {(field.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      </div>
    );
  }
  if (field.type === "textarea") {
    return (
      <div className="form-field" data-testid={`mcs-field-${field.id}`}>
        <Label>{field.label}</Label>
        <textarea className="brutalist-input min-h-[70px] resize-y" value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={field.placeholder || ""} />
      </div>
    );
  }
  return (
    <div className="form-field" data-testid={`mcs-field-${field.id}`}>
      <Label>{field.label}</Label>
      <input className="brutalist-input" type={field.type === "number" ? "number" : "text"} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={field.placeholder || ""} />
    </div>
  );
}

export default function MCSFormWizard() {
  const { formId } = useParams();
  const { user } = useAuth();
  const config = MCS_FORMS[`mcs-${formId}`];

  const [step, setStep] = useState(1); // 1=project+form, 2=review+generate
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [draftSaved, setDraftSaved] = useState(false);
  const [signature, setSignature] = useState("");
  const { showModal, modalCreditType, promptBuyCredits, closeModal, buyCredits } = useCreditPrompt();

  const draftKey = formId ? `draft_mcs_${formId}` : null;

  // Project info shared across all MCS forms
  const [project, setProject] = useState({
    project_ref: "", installation_address: "", installation_postcode: "",
    client_name: "", client_email: "", completed_by: user?.name || "", cert_date: null,
    contractor_trading_title: user?.company || "", contractor_email: user?.email || "",
    contractor_phone: "", contractor_address: "", contractor_postcode: "", contractor_reg_no: "",
  });

  // Form-specific field values
  const [fields, setFields] = useState({});

  // Dynamic strings for Solar PV Array Test
  const EMPTY_STRING = { module_amount: "", voc: "", isc: "", ir_pos: "", ir_neg: "" };
  const [strings, setStrings] = useState([{ ...EMPTY_STRING }, { ...EMPTY_STRING }]);
  const addString = () => { if (strings.length < (config?.maxStrings || 15)) setStrings((p) => [...p, { ...EMPTY_STRING }]); };
  const removeString = (idx) => { if (strings.length > 1) setStrings((p) => p.filter((_, i) => i !== idx)); };
  const updateString = (idx, field, val) => setStrings((p) => p.map((s, i) => i === idx ? { ...s, [field]: val } : s));

  // Auto-save draft every 10s
  useEffect(() => {
    if (!draftKey || completed) return;
    const timer = setInterval(() => {
      saveDraft(draftKey, { project, fields, strings }).then(() => setDraftSaved(true));
    }, 10000);
    return () => clearInterval(timer);
  }, [project, fields, strings, draftKey, completed]);

  const manualSaveDraft = async () => {
    if (!draftKey) return;
    await saveDraft(draftKey, { project, fields, strings });
    setDraftSaved(true);
    toast.success("Draft saved locally");
  };

  const [editCertId, setEditCertId] = useState(null);

  // Auto-fill contractor + load drafts
  useEffect(() => {
    // Check for edit data first
    const editData = sessionStorage.getItem("edit_cert_data");
    if (editData) {
      try {
        const cert = JSON.parse(editData);
        sessionStorage.removeItem("edit_cert_data");
        setEditCertId(cert._edit_id);
        setProject((p) => ({ ...p, ...cert, cert_date: cert.cert_date ? new Date(cert.cert_date.split("/").reverse().join("-")) : null }));
        if (cert.form_fields) setFields(cert.form_fields);
        toast.info("Editing document — update and regenerate for free");
        return;
      } catch { /* ignore */ }
    }

    // Check for duplicate data
    const dupData = sessionStorage.getItem("duplicate_cert_data");
    if (dupData) {
      try {
        const cert = JSON.parse(dupData);
        sessionStorage.removeItem("duplicate_cert_data");
        setProject((p) => ({
          ...p,
          project_ref: "",
          installation_address: cert.installation_address || p.installation_address,
          installation_postcode: cert.installation_postcode || p.installation_postcode,
          client_name: cert.client_name || p.client_name,
          client_email: cert.client_email || p.client_email,
          completed_by: cert.completed_by || p.completed_by,
          contractor_trading_title: cert.contractor_trading_title || p.contractor_trading_title,
          contractor_email: cert.contractor_email || p.contractor_email,
          contractor_phone: cert.contractor_phone || p.contractor_phone,
          contractor_address: cert.contractor_address || p.contractor_address,
          contractor_postcode: cert.contractor_postcode || p.contractor_postcode,
          contractor_reg_no: cert.contractor_reg_no || p.contractor_reg_no,
        }));
        if (cert.form_fields) setFields(cert.form_fields);
        toast.success("Certificate data loaded — update details as needed");
        return;
      } catch { /* ignore */ }
    }

    const repeatData = sessionStorage.getItem("repeat_cert_data");
    if (repeatData) {
      try {
        const prefill = JSON.parse(repeatData);
        sessionStorage.removeItem("repeat_cert_data");
        setProject((p) => ({ ...p, ...prefill, project_ref: "" }));
        toast.success("Customer and address details pre-filled from previous job");
        return;
      } catch { /* ignore */ }
    }

    // Try loading a saved draft
    if (draftKey) {
      loadDraft(draftKey).then((draft) => {
        if (draft) {
          if (draft.project) setProject((p) => ({ ...p, ...draft.project }));
          if (draft.fields) setFields(draft.fields);
          if (draft.strings?.length) setStrings(draft.strings);
          toast.info("Restored from saved draft");
          return;
        }
      }).catch(() => {});
    }

    authFetch(`${API}/api/user/contractor-details`, { credentials: "include" })
      .then((r) => r.ok ? r.json() : {})
      .then((saved) => {
        if (saved && saved.contractor_trading_title) {
          setProject((prev) => ({
            ...prev,
            contractor_trading_title: saved.contractor_trading_title || prev.contractor_trading_title,
            contractor_email: saved.contractor_email || prev.contractor_email,
            contractor_phone: saved.contractor_phone || prev.contractor_phone,
            contractor_address: saved.contractor_address || prev.contractor_address,
            contractor_postcode: saved.contractor_postcode || prev.contractor_postcode,
            contractor_reg_no: saved.contractor_reg_no || prev.contractor_reg_no,
          }));
        }
      })
      .catch(() => {});
  }, [draftKey]);

  if (!config) return <Navigate to="/certificates" replace />;

  const setP = (f) => (e) => setProject((p) => ({ ...p, [f]: e.target.value }));
  const setF = (id, val) => setFields((prev) => ({ ...prev, [id]: val }));

  const selectAll = (section) => {
    const updates = {};
    section.fields.forEach((f) => { if (f.type === "checkbox") updates[f.id] = true; });
    setFields((prev) => ({ ...prev, ...updates }));
  };

  const deselectAll = (section) => {
    const updates = {};
    section.fields.forEach((f) => { if (f.type === "checkbox") updates[f.id] = false; });
    setFields((prev) => ({ ...prev, ...updates }));
  };

  const totalSteps = 2;
  const STEPS = [{ num: 1, label: "Details & Form" }, { num: 2, label: "Review & Generate" }];

  const buildPayload = () => {
    const payload = {
      ...project,
      cert_date: project.cert_date ? format(project.cert_date, "dd/MM/yyyy") : "",
      form_type: config.type,
      form_title: config.title,
      signature,
      form_fields: { ...fields, ...(config.hasDynamicStrings ? { strings } : {}) },
      form_sections: config.sections.map((s) => ({
        title: s.title,
        fields: s.fields.map((f) => ({ id: f.id, label: f.label, type: f.type, value: fields[f.id] ?? "" })),
      })),
    };
    // Add dynamic string results as a section
    if (config.hasDynamicStrings) {
      payload.form_sections.splice(1, 0, {
        title: "String Test Results",
        fields: strings.flatMap((s, i) => [
          { id: `s${i+1}_modules`, label: `String ${i+1} Module Amount`, type: "text", value: s.module_amount },
          { id: `s${i+1}_voc`, label: `String ${i+1} Voc (V)`, type: "text", value: s.voc },
          { id: `s${i+1}_isc`, label: `String ${i+1} Isc (A)`, type: "text", value: s.isc },
          { id: `s${i+1}_ir_pos`, label: `String ${i+1} IR DC+ to Earth (MΩ)`, type: "text", value: s.ir_pos },
          { id: `s${i+1}_ir_neg`, label: `String ${i+1} IR DC- to Earth (MΩ)`, type: "text", value: s.ir_neg },
        ]),
      });
    }
    return payload;
  };

  const handlePreview = async () => {
    setPreviewing(true);
    try {
      const res = await authFetch(`${API}/api/certificates/mcs/preview`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        credentials: "include", body: JSON.stringify(buildPayload()),
      });
      if (!res.ok) throw new Error("Preview failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      setPreviewUrl(url);
      setShowPreview(true);
    } catch (err) { toast.error(err.message); }
    finally { setPreviewing(false); }
  };

  const handleGenerate = async () => {
    if (!navigator.onLine) {
      await queueForSync({ endpoint: "/api/certificates/mcs", method: "POST", payload: buildPayload(), formTitle: config.title });
      await manualSaveDraft();
      toast.success("You're offline — certificate queued and will auto-submit when back online.");
      return;
    }
    setSubmitting(true);
    try {
      const isEdit = !!editCertId;
      const url = isEdit ? `${API}/api/certificates/${editCertId}` : `${API}/api/certificates/mcs`;
      const method = isEdit ? "PUT" : "POST";
      const res = await authFetch(url, {
        method, headers: { "Content-Type": "application/json" },
        credentials: "include", body: JSON.stringify(buildPayload()),
      });
      if (res.status === 402) {
        promptBuyCredits("mcs");
        return;
      }
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || "Failed");
      const data = await res.json();
      const certId = data.certificate_id || editCertId;
      setPdfUrl(`${API}/api/certificates/domestic-eic/${certId}/download`);
      setCompleted(true);
      toast.success(isEdit ? "Document updated!" : "Document generated! 1 credit used.");
      if (draftKey) deleteDraft(draftKey).catch(() => {});
      // Save contractor details
      authFetch(`${API}/api/user/contractor-details`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({
          contractor_trading_title: project.contractor_trading_title,
          contractor_email: project.contractor_email,
          contractor_phone: project.contractor_phone,
          contractor_address: project.contractor_address,
          contractor_postcode: project.contractor_postcode,
          contractor_reg_no: project.contractor_reg_no,
        }),
      }).catch(() => {});
    } catch (err) {
      if (!navigator.onLine || err.message === "Failed to fetch") {
        await queueForSync({ endpoint: "/api/certificates/mcs", method: "POST", payload: buildPayload(), formTitle: config.title });
        toast.success("Network lost — certificate queued for auto-sync.");
      } else {
        toast.error(err.message);
      }
    }
    finally { setSubmitting(false); }
  };

  if (completed) {
    return (
      <div className="min-h-screen bg-[#FAFAFA]"><AppHeader />
        <div className="max-w-3xl mx-auto px-4 py-12">
          <div className="form-container text-center" data-testid="mcs-complete">
            <CheckCircle className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Document Generated</h2>
            <p className="text-sm text-[#71717A] mb-6">Your {config.title} has been created.</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              {pdfUrl && <a href={pdfUrl} target="_blank" rel="noopener noreferrer" data-testid="mcs-download-pdf" className="brutalist-btn flex items-center justify-center gap-2"><FileDown className="w-4 h-4" /> Download PDF</a>}
              <a href="/dashboard" className="brutalist-btn-outline flex items-center justify-center gap-2">Back to Dashboard</a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="mcs-form-wizard">
      <AppHeader />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex items-center gap-3 mb-6">
          <Leaf className="w-6 h-6 text-[#22C55E]" />
          <h1 className="text-xl sm:text-2xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>{config.title}</h1>
        </div>

        {/* Step indicator */}
        <div className="flex items-center justify-between mb-8" data-testid="mcs-step-indicator">
          {STEPS.map((s, i) => (
            <div key={s.num} className="flex items-center flex-1">
              <div className="flex flex-col items-center">
                <div className={`w-10 h-10 flex items-center justify-center border-2 text-sm font-bold transition-all ${step > s.num ? "bg-[#22C55E] text-white border-[#22C55E]" : step === s.num ? "bg-[#22C55E] text-white border-[#22C55E]" : "bg-white text-[#B8B9BD] border-[#E4E4E7]"}`}>
                  {step > s.num ? <Check className="w-5 h-5" /> : s.num}
                </div>
                <span className={`text-xs mt-2 font-medium text-center hidden sm:block ${step >= s.num ? "text-[#0A0A0A]" : "text-[#B8B9BD]"}`} style={{ fontFamily: 'Outfit, sans-serif' }}>{s.label}</span>
              </div>
              {i < STEPS.length - 1 && <div className={`flex-1 h-0.5 mx-2 sm:mx-4 ${step > s.num ? "bg-[#22C55E]" : "bg-[#E4E4E7]"}`} />}
            </div>
          ))}
        </div>

        {step === 1 && (
          <div className="form-container max-w-3xl mx-auto" data-testid="mcs-step-1">
            <TemplateBar formType={config?.type || ""} onLoad={(data) => {
              if (data.project) setProject(p => ({ ...p, ...data.project }));
              if (data.fields) setFields(f => ({ ...f, ...data.fields }));
            }} />
            {/* Project Details — shared */}
            <div className="border-b-2 border-[#E4E4E7] pb-5 mb-5">
              <h3 className="text-sm font-bold text-[#22C55E] uppercase tracking-[0.15em] mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>Project Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div><Label>Project reference</Label><input className="brutalist-input" value={project.project_ref} onChange={setP("project_ref")} /></div>
                <div><Label>Client name</Label><input className="brutalist-input" value={project.client_name} onChange={setP("client_name")} /></div>
                <div><Label>Client email</Label><input className="brutalist-input" type="email" value={project.client_email} onChange={setP("client_email")} placeholder="optional" /></div>
                <div><Label>Completed by</Label><input className="brutalist-input" value={project.completed_by} onChange={setP("completed_by")} /></div>
                <div className="md:col-span-2"><Label>Installation address</Label><input className="brutalist-input" value={project.installation_address} onChange={setP("installation_address")} /></div>
                <div><Label>Postcode</Label><input className="brutalist-input" value={project.installation_postcode} onChange={setP("installation_postcode")} /></div>
                <div>
                  <Label>Date</Label>
                  <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                    <PopoverTrigger asChild>
                      <button className="brutalist-input flex items-center justify-between cursor-pointer w-full" type="button">
                        <span className={project.cert_date ? "text-[#0A0A0A]" : "text-[#9CA3AF]"}>
                          {project.cert_date ? format(project.cert_date, "dd/MM/yyyy") : "Select date"}
                        </span>
                        <CalendarIcon className="w-4 h-4 text-[#71717A]" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 border-2 border-[#0A0A0A] rounded-none" align="start">
                      <Calendar mode="single" selected={project.cert_date} onSelect={(d) => { setProject((p) => ({ ...p, cert_date: d })); setCalendarOpen(false); }} />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
            </div>

            {/* Form-specific sections */}
            {config.sections.map((section, si) => {
              const isAllCheckboxes = section.fields.every((f) => f.type === "checkbox");
              const checkboxFields = section.fields.filter((f) => f.type === "checkbox");
              const checkboxCount = checkboxFields.length;
              const checkedCount = checkboxFields.filter((f) => fields[f.id]).length;
              const showBulkButtons = checkboxCount > 2;
              return (
              <div key={si} className="border-b-2 border-[#E4E4E7] pb-5 mb-5 last:border-0">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-bold text-[#22C55E] uppercase tracking-[0.15em]" style={{ fontFamily: 'Outfit, sans-serif' }}>{section.title}</h3>
                  {showBulkButtons && (
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-[#71717A]">{checkedCount}/{checkboxCount}</span>
                      <button type="button" data-testid={`mcs-select-all-${si}`} onClick={() => selectAll(section)}
                        className="text-xs font-bold text-[#22C55E] hover:underline">Select All</button>
                      <button type="button" data-testid={`mcs-deselect-all-${si}`} onClick={() => deselectAll(section)}
                        className="text-xs font-bold text-[#71717A] hover:underline">Deselect All</button>
                    </div>
                  )}
                </div>
                {isAllCheckboxes ? (
                  <div className="space-y-0">
                    {section.fields.map((f) => (
                      <FormField key={f.id} field={f} value={fields[f.id]} onChange={(v) => setF(f.id, v)} />
                    ))}
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {section.fields.map((f) => (
                      f.type === "checkbox" ? (
                        <div key={f.id} className="md:col-span-2">
                          <FormField field={f} value={fields[f.id]} onChange={(v) => setF(f.id, v)} />
                        </div>
                      ) : (
                        <div key={f.id} className={f.type === "textarea" ? "md:col-span-2" : ""}>
                          <FormField field={f} value={fields[f.id]} onChange={(v) => setF(f.id, v)} />
                        </div>
                      )
                    ))}
                  </div>
                )}
              </div>
            );})}

            {/* Dynamic String Test Results for Solar PV Array Test */}
            {config.hasDynamicStrings && (
              <div className="border-b-2 border-[#E4E4E7] pb-5 mb-5">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-bold text-[#22C55E] uppercase tracking-[0.15em]" style={{ fontFamily: 'Outfit, sans-serif' }}>String Test Results</h3>
                  <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{strings.length} / {config.maxStrings || 15} strings</span>
                </div>
                <div className="space-y-3">
                  {strings.map((s, idx) => (
                    <div key={idx} className="border-2 p-3" style={{ borderColor: 'var(--border-light)', backgroundColor: 'var(--bg-card)' }}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-bold text-[#22C55E]" style={{ fontFamily: 'Outfit, sans-serif' }}>String {idx + 1}</span>
                        {strings.length > 1 && (
                          <button type="button" onClick={() => removeString(idx)} className="text-red-500 hover:text-red-700">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                        <div>
                          <label className="text-[10px] uppercase font-bold" style={{ color: 'var(--text-muted)' }}>Module Amount</label>
                          <input className="brutalist-input !text-xs !py-1.5" value={s.module_amount} onChange={(e) => updateString(idx, "module_amount", e.target.value)} data-testid={`string-${idx}-modules`} />
                        </div>
                        <div>
                          <label className="text-[10px] uppercase font-bold" style={{ color: 'var(--text-muted)' }}>Voc (V)</label>
                          <input className="brutalist-input !text-xs !py-1.5" value={s.voc} onChange={(e) => updateString(idx, "voc", e.target.value)} />
                        </div>
                        <div>
                          <label className="text-[10px] uppercase font-bold" style={{ color: 'var(--text-muted)' }}>Isc (A)</label>
                          <input className="brutalist-input !text-xs !py-1.5" value={s.isc} onChange={(e) => updateString(idx, "isc", e.target.value)} />
                        </div>
                        <div>
                          <label className="text-[10px] uppercase font-bold" style={{ color: 'var(--text-muted)' }}>IR DC+ to Earth (MΩ)</label>
                          <input className="brutalist-input !text-xs !py-1.5" value={s.ir_pos} onChange={(e) => updateString(idx, "ir_pos", e.target.value)} />
                        </div>
                        <div>
                          <label className="text-[10px] uppercase font-bold" style={{ color: 'var(--text-muted)' }}>IR DC- to Earth (MΩ)</label>
                          <input className="brutalist-input !text-xs !py-1.5" value={s.ir_neg} onChange={(e) => updateString(idx, "ir_neg", e.target.value)} />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                {strings.length < (config.maxStrings || 15) && (
                  <button type="button" onClick={addString} data-testid="add-string-btn"
                    className="brutalist-btn-outline !px-4 !py-2 flex items-center gap-2 text-xs mt-3">
                    <Plus className="w-4 h-4" /> + String
                  </button>
                )}
              </div>
            )}

            <div className="flex justify-between items-center">
              <button type="button" onClick={manualSaveDraft} className="brutalist-btn-outline !px-3 !py-1.5 text-xs flex items-center gap-1.5" data-testid="mcs-save-draft-btn">
                <Save className="w-3.5 h-3.5" /> {draftSaved ? "Draft Saved" : "Save Draft"}
              </button>
              <button data-testid="mcs-step-1-next" onClick={() => setStep(2)} className="brutalist-btn flex items-center gap-2">
                Review <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="form-container max-w-3xl mx-auto" data-testid="mcs-step-2">
            <h2 className="text-xl font-bold mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>Review & Generate</h2>
            <p className="text-xs text-[#71717A] mb-6">Review all details, then generate your document.</p>

            {/* Project summary */}
            <div className="border-b-2 border-[#E4E4E7] pb-4 mb-4">
              <h3 className="text-sm font-bold text-[#22C55E] uppercase tracking-[0.15em] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Project Details</h3>
              {[["Reference", project.project_ref], ["Client", project.client_name], ["Address", project.installation_address], ["Postcode", project.installation_postcode],
                ["Completed by", project.completed_by], ["Date", project.cert_date ? format(project.cert_date, "dd/MM/yyyy") : ""],
              ].map(([l, v]) => v ? (
                <div key={l} className="flex justify-between py-1 text-sm">
                  <span className="text-[#71717A]">{l}</span>
                  <span className="font-semibold text-right max-w-[60%]">{v}</span>
                </div>
              ) : null)}
            </div>

            {/* Section summaries */}
            {config.sections.map((section, si) => (
              <div key={si} className="border-b-2 border-[#E4E4E7] pb-4 mb-4 last:border-0">
                <h3 className="text-sm font-bold text-[#22C55E] uppercase tracking-[0.15em] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>{section.title}</h3>
                {section.fields.map((f) => {
                  const val = fields[f.id];
                  if (f.type === "checkbox") {
                    return (
                      <div key={f.id} className="flex items-center gap-2 py-0.5 text-sm">
                        <span className={`font-bold ${val ? "text-emerald-600" : "text-[#B8B9BD]"}`}>{val ? "✓" : "—"}</span>
                        <span className={val ? "text-[#0A0A0A]" : "text-[#B8B9BD]"}>{f.label}</span>
                      </div>
                    );
                  }
                  if (!val) return null;
                  return (
                    <div key={f.id} className="flex justify-between py-1 text-sm">
                      <span className="text-[#71717A]">{f.label}</span>
                      <span className="font-semibold text-right max-w-[60%]">{String(val)}</span>
                    </div>
                  );
                })}
              </div>
            ))}

            {/* Dynamic strings review */}
            {config.hasDynamicStrings && strings.some((s) => s.voc || s.isc || s.module_amount) && (
              <div className="border-b-2 border-[#E4E4E7] pb-4 mb-4">
                <h3 className="text-sm font-bold text-[#22C55E] uppercase tracking-[0.15em] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>String Test Results ({strings.length} strings)</h3>
                {strings.map((s, idx) => (
                  <div key={idx} className="py-1.5 border-b last:border-0" style={{ borderColor: 'var(--border-light)' }}>
                    <p className="text-xs font-bold text-[#22C55E] mb-1">String {idx + 1}</p>
                    <div className="grid grid-cols-5 gap-2 text-xs">
                      {[["Modules", s.module_amount], ["Voc", s.voc ? `${s.voc} V` : ""], ["Isc", s.isc ? `${s.isc} A` : ""], ["IR DC+", s.ir_pos ? `${s.ir_pos} MΩ` : ""], ["IR DC-", s.ir_neg ? `${s.ir_neg} MΩ` : ""]].map(([l, v]) => (
                        <div key={l}><span style={{ color: 'var(--text-muted)' }}>{l}: </span><span className="font-semibold">{v || "—"}</span></div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Signature & Save Template */}
            <div className="border-t-2 pt-5 mt-4 space-y-4" style={{ borderColor: 'var(--border-light)' }}>
              <SignaturePad label="Engineer's Signature" value={signature} onChange={setSignature} />
              <SaveTemplateButton formType={config?.type || ""} getFormData={() => ({ project, fields })} />
            </div>

            <div className="flex flex-col sm:flex-row justify-between gap-3 mt-4">
              <button data-testid="mcs-step-2-prev" onClick={() => setStep(1)} className="brutalist-btn-outline flex items-center gap-2">
                <ArrowLeft className="w-4 h-4" /> Previous
              </button>
              <div className="flex gap-3">
                <button data-testid="mcs-preview-btn" onClick={handlePreview} disabled={previewing} className="brutalist-btn-outline flex items-center gap-2">
                  {previewing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />} Preview PDF
                </button>
                <button data-testid="mcs-generate-btn" onClick={handleGenerate} disabled={submitting} className="brutalist-btn flex items-center gap-2" style={{ backgroundColor: "#22C55E" }}>
                  {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Generating...</> : <><FileDown className="w-4 h-4" /> Generate Document</>}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      <BuyCreditsModal open={showModal} onClose={closeModal} creditType={modalCreditType} onBuy={buyCredits} />
      <PdfPreviewModal open={showPreview} onClose={() => { setShowPreview(false); if (previewUrl) URL.revokeObjectURL(previewUrl); }} pdfUrl={previewUrl} loading={previewing} />
    </div>
  );
}
